/**
 * The Apple button on `/sign-in`.
 *
 * A plain `<button>` on purpose. Apple's guidelines allow exactly two color
 * pairs, black and white, so `@sportsmet/ui`'s `Button` cannot be used here:
 * it applies the sportsmet palette, and any tint of it is a violation. The
 * chrome comes from one shared stylesheet, `@sportsmet/auth-web/appleButton.css`,
 * imported once in `src/index.css` so both web apps render the same button.
 */
import { startAppleSignIn } from "@sportsmet/auth-web";
import { type ReactNode, useState } from "react";

/**
 * Apple's mark, inline so `currentColor` carries the button's own color. That
 * makes "the logo and the title are always the same color", which is Apple's
 * rule, impossible to get wrong.
 */
const AppleLogo = (): ReactNode => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={18}
    viewBox="0 0 24 24"
    width={18}
  >
    <path
      fill="currentColor"
      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
    />
  </svg>
);

export type typeAppleSignInButtonProps = {
  /** apple.webServicesId from POST /oauth/provider-config-get. Never empty. */
  webServicesId: string;
  onIdToken: (args: { idToken: string }) => void;
  /** Called only for a real failure. A cancellation calls nothing. */
  onFailed: () => void;
};

export const AppleSignInButton = ({
  webServicesId,
  onIdToken,
  onFailed,
}: typeAppleSignInButtonProps): ReactNode => {
  const [isBusy, setIsBusy] = useState(false);

  const onClick = async (): Promise<void> => {
    setIsBusy(true);
    try {
      const redirectUri = `${window.location.origin}/sign-in`;
      const result = await startAppleSignIn({ webServicesId, redirectUri });
      switch (result.kind) {
        case "idToken":
          onIdToken({ idToken: result.idToken });
          break;
        case "canceled":
          // Closing the popup is not an error. The screen stays as it was.
          break;
        case "failed":
          onFailed();
          break;
      }
    } catch {
      // The script loads inside the click handler, so a CDN failure happens
      // after a press. Report it the same way a provider popup failure does.
      onFailed();
    } finally {
      setIsBusy(false);
    }
  };

  // Busy is `disabled` only. Apple's popup already covers this button, so a
  // spinner underneath it would be invisible, and changing the title mid-press
  // would break Apple's three-permitted-strings rule.
  return (
    <button
      aria-label="Continue with Apple"
      className="sm-apple-signin-button"
      disabled={isBusy}
      onClick={() => {
        void onClick();
      }}
      type="button"
    >
      <AppleLogo />
      <span>Continue with Apple</span>
    </button>
  );
};
